/**
Copyright (c) 2022–2026 Podstack.ai
All rights reserved.
Proprietary and Confidential.
Unauthorized copying of this file, via any medium, is strictly prohibited.
This file is part of the Podstack Platform.
*/

import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { assignmentApi } from '../services/assignmentApi';

type Tab = 'pods' | 'helm';

const StatusBadge: React.FC<{ s: string }> = ({ s }) => {
  const styles: Record<string, string> = {
    active: 'bg-green-100 text-green-800',
    suspended: 'bg-yellow-100 text-yellow-800',
    removed: 'bg-gray-100 text-gray-800',
  };
  return (
    <span className={`px-2 py-1 rounded text-xs font-medium ${styles[s] || 'bg-gray-100 text-gray-800'}`}>
      {s}
    </span>
  );
};

const AssignedResources: React.FC = () => {
  const [params, setParams] = useSearchParams();
  const tab: Tab = (params.get('tab') as Tab) || 'pods';

  const pods = useQuery({
    queryKey: ['my-assigned-pods'],
    queryFn: assignmentApi.listPods,
    refetchInterval: 30000,
    enabled: tab === 'pods',
  });

  const helm = useQuery({
    queryKey: ['my-assigned-helm'],
    queryFn: assignmentApi.listHelm,
    refetchInterval: 30000,
    enabled: tab === 'helm',
  });

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold text-white mb-6">Assigned Resources</h1>

      {/* Tabs */}
      <div className="flex gap-4 border-b border-gray-700 mb-6">
        <button
          onClick={() => setParams({ tab: 'pods' })}
          className={`pb-2 text-sm font-medium transition-colors ${
            tab === 'pods'
              ? 'border-b-2 border-primary-500 text-primary-500'
              : 'text-gray-400 hover:text-gray-200'
          }`}
        >
          Pods
        </button>
        <button
          onClick={() => setParams({ tab: 'helm' })}
          className={`pb-2 text-sm font-medium transition-colors ${
            tab === 'helm'
              ? 'border-b-2 border-primary-500 text-primary-500'
              : 'text-gray-400 hover:text-gray-200'
          }`}
        >
          Helm Releases
        </button>
      </div>

      {/* Pods Tab */}
      {tab === 'pods' && (
        <>
          {pods.isLoading && (
            <div className="text-gray-400 text-sm">Loading assigned pods...</div>
          )}
          {pods.isError && (
            <div className="text-red-400 text-sm">Failed to load assigned pods.</div>
          )}
          {pods.data && pods.data.data.length === 0 && (
            <div className="text-gray-400 text-sm">No assigned pods.</div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {(pods.data?.data || []).map((p) => (
              <Link
                to={`/assignments/pods/${p.id}`}
                key={p.id}
                className="border border-gray-700 rounded-lg p-4 hover:border-gray-500 hover:shadow-md transition-all bg-gray-900"
              >
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <div className="font-medium text-white">{p.display_name}</div>
                    <div className="text-sm text-gray-400 mt-0.5">
                      {p.namespace} / {p.pod_name}
                    </div>
                  </div>
                  <StatusBadge s={p.status} />
                </div>
                <div className="text-sm text-gray-300">
                  ₹{p.monthly_cost.toFixed(2)} / month
                </div>
                {p.status === 'suspended' && (
                  <div className="mt-2 text-xs bg-yellow-900/30 text-yellow-400 border border-yellow-700/50 p-2 rounded">
                    Suspended due to low balance.{' '}
                    <Link to="/billing/payments?action=topup" className="underline" onClick={(e) => e.stopPropagation()}>
                      Top up wallet
                    </Link>
                    .
                  </div>
                )}
              </Link>
            ))}
          </div>
        </>
      )}

      {/* Helm Releases Tab */}
      {tab === 'helm' && (
        <>
          {helm.isLoading && (
            <div className="text-gray-400 text-sm">Loading assigned helm releases...</div>
          )}
          {helm.isError && (
            <div className="text-red-400 text-sm">Failed to load assigned helm releases.</div>
          )}
          {helm.data && helm.data.data.length === 0 && (
            <div className="text-gray-400 text-sm">No assigned helm releases.</div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {(helm.data?.data || []).map((r) => (
              <Link
                to={`/assignments/helm/${r.id}`}
                key={r.id}
                className="border border-gray-700 rounded-lg p-4 hover:border-gray-500 hover:shadow-md transition-all bg-gray-900"
              >
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <div className="font-medium text-white">{r.display_name}</div>
                    <div className="text-sm text-gray-400 mt-0.5">
                      {r.namespace} / {r.release_name} - {r.chart_name}@{r.chart_version}
                    </div>
                  </div>
                  <StatusBadge s={r.status} />
                </div>
                <div className="text-sm text-gray-300">
                  ₹{r.monthly_cost.toFixed(2)} / month
                </div>
                {r.status === 'suspended' && (
                  <div className="mt-2 text-xs bg-yellow-900/30 text-yellow-400 border border-yellow-700/50 p-2 rounded">
                    Suspended due to low balance.{' '}
                    <Link to="/billing/payments?action=topup" className="underline" onClick={(e) => e.stopPropagation()}>
                      Top up wallet
                    </Link>
                    .
                  </div>
                )}
              </Link>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default AssignedResources;
